import {
  createContext,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { sseUrl } from "./api";
import { EventSourcePolyfill } from "event-source-polyfill";
import { RealTimeContextType } from "./utils/RealTimeContextType";

export const SSEContext = createContext<RealTimeContextType | undefined>(
  undefined
);

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds

export const SSEProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [message, setMessage] = useState<string>("");
  const [isConnected, setIsconnected] = useState(false);
  const [isConnecting, setIsconnecting] = useState(false);
  
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRef = useRef<string | null>(null);
  const shouldReconnectRef = useRef(true);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    setIsconnected(false);
    setIsconnecting(false);
  }, [eventSource]);

  const attemptReconnect = useCallback(() => {
    if (!shouldReconnectRef.current || !tokenRef.current) {
      return;
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error("SSE: Max reconnection attempts reached");
      setIsconnecting(false);
      return;
    }

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY
    );

    console.log(
      `SSE: Attempting reconnection ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connect(tokenRef.current);
    }, delay);
  }, []);

  const connect = useCallback(
    (token: string | null) => {
      if (isConnected) {
        console.log("SSE: Already connected");
        return;
      }
      if (isConnecting) {
        console.log("SSE: Connection in progress");
        return;
      }

      if (!token) {
        console.log("SSE: No authorization token provided");
        return;
      }

      setIsconnecting(true);
      tokenRef.current = token;
      shouldReconnectRef.current = true;

      console.log("SSE: Connecting...");

      try {
        const es = new EventSourcePolyfill(sseUrl + `/subscribe`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
          heartbeatTimeout: 45000, // 45 seconds
        });

        es.onopen = () => {
          setIsconnected(true);
          setIsconnecting(false);
          reconnectAttemptsRef.current = 0; // Reset on successful connection
          console.log("SSE: Connected successfully");
        };

        es.onmessage = (event: { data: SetStateAction<string> }) => {
          setMessage(event.data);
        };

        es.onerror = (error: any) => {
          console.error("SSE: Connection error", error);
          
          // Clean up current connection
          if (es.readyState === EventSource.CLOSED) {
            console.log("SSE: Connection closed by server");
          }
          
          setIsconnected(false);
          setIsconnecting(false);
          
          // Close the failed connection
          try {
            es.close();
          } catch (e) {
            console.error("SSE: Error closing connection", e);
          }
          
          setEventSource(null);

          // Attempt reconnection if enabled
          if (shouldReconnectRef.current) {
            attemptReconnect();
          }
        };

        setEventSource(es);
      } catch (error) {
        console.error("SSE: Failed to create connection", error);
        setIsconnecting(false);
        setIsconnected(false);
        
        if (shouldReconnectRef.current) {
          attemptReconnect();
        }
      }
    },
    [isConnected, isConnecting, attemptReconnect]
  );

  const disconnect = useCallback(() => {
    console.log("SSE: Disconnecting...");
    shouldReconnectRef.current = false;
    reconnectAttemptsRef.current = 0;
    tokenRef.current = null;
    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // SSE is one-way, so sendMessage is a no-op
  const sendMessage = (_: string | object) => {
    console.log("SSE: Client-to-server messaging not supported");
  };

  return (
    <SSEContext.Provider
      value={{
        connect,
        disconnect,
        sendMessage,
        message,
        isConnected,
        isConnecting,
      }}
    >
      {children}
    </SSEContext.Provider>
  );
};
